import React, { useContext } from 'react';
import { styled } from 'styled-components';
import checkmark from '@ingka/ssr-icon/paths/checkmark';
import SSRIcon from '@ingka/ssr-icon';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { mediaQueries } from '../styles/mediaQueries';
import colors from '../styles/colors';
import PayOrder from '../components/PayOrder';
import { useStateSelector } from '../store/';
import { ViewContext } from '../context';
import RedirectButton from '../components/RedirectButton';
import { getQueryParamsFromURL } from '../helpers/urlHelper';
import StickyHeader from '../components/StickyHeader';
import StickyFooter from '../components/StickyFooter';
import Button from '@ingka/button';
import { StyledWrapper } from '../components/PageLayout';
import { sendEventToParent } from '../eventPublisher';
import { selectServiceForTheSelectedTimeSlot } from '../reducers/selectors';

const StyledContent = styled.div`
  text-align: center;
`;

const StyledIcon = styled(SSRIcon)`
  &&& {
    color: ${colors.semantic.positive};
    width: 88px;
    height: 88px;
  }
`;

const StyledHeading = styled(Text)`
  &&& {
    margin-bottom: 1rem;
  }
`;

const StyledParagraph = styled(Text)`
  &&& {
    margin-bottom: 1rem;

    ${mediaQueries.portraitTabletUp} {
      margin-bottom: 2.5rem;
    }
  }
`;

const QuestionsConfirmed: React.FC = () => {
  const { t } = useTranslation();
  const view = useContext(ViewContext);
  const redirectUrl = getQueryParamsFromURL().redirectUrl;
  const orderNo = useStateSelector((state) => state.app.booking.orderNo);
  const payOrderLink = useStateSelector(
    (state) => state.app.booking.payOrderLink,
  );
  const payOrderLinkEnabled = useStateSelector(
    (state) => state.app.booking.payOrderLinkEnabled,
  );
  const email = useStateSelector((state) => state.contact.email);
  const service = useStateSelector(selectServiceForTheSelectedTimeSlot);

  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';

  const showPayOrder =
    !isViewApp &&
    payOrderLinkEnabled &&
    !!service &&
    service.customerPrice.price > 0;

  return (
    <>
      <PageTitle title={t('pages.questionsConfirmed.pageTitle')} />
      {isViewAlt && <StickyHeader customEventName={'BOKA/QUESTIONS_CLOSED'} />}
      <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
        <PageContent>
          <StyledContent data-cy="questions-confirmed">
            <StyledIcon paths={checkmark} />
            <StyledHeading
              tagName={isViewAlt ? 'h2' : 'h1'}
              textStyle="Heading.L"
            >
              {t('pages.questionsConfirmed.questionsConfirmedTitle')}
            </StyledHeading>
            <StyledParagraph tagName="p" textStyle="Body.L">
              {t('pages.questionsConfirmed.questionsConfirmedDescription')}
            </StyledParagraph>
          </StyledContent>
          {service && showPayOrder && (
            <PayOrder
              price={service.customerPrice}
              currencyCode={service.displayPrice.currencyCode}
              payOrderLink={payOrderLink}
              orderNo={orderNo}
              email={email}
              page="questions"
              view={view}
            />
          )}
          {(isViewAlt || isViewApp) && (
            <StickyFooter>
              {isViewApp && (
                <RedirectButton
                  dataCy="questions-confirmed-back-button"
                  url={`${redirectUrl}?status=success`}
                />
              )}
              {isViewAlt && (
                <Button
                  text={t('pages.questionsConfirmed.confirmAndCloseBtnText')}
                  type="emphasised"
                  aria-label={t(
                    'components.stickyFooter.ariaLabelReturnAndClose',
                  )}
                  data-cy="confirm-and-close-button"
                  onClick={() =>
                    sendEventToParent('BOKA/QUESTIONS_CLOSED', view)
                  }
                />
              )}
            </StickyFooter>
          )}
        </PageContent>
      </StyledWrapper>
    </>
  );
};

export default QuestionsConfirmed;
